/**
 * Maps task keys to their dedicated React Router routes.
 * Unknown task keys fall through to the coming-soon toast (handled in GetReadyPage).
 */
export const TASK_ROUTE_BY_KEY: Readonly<Record<string, string>> = {
	fill_health_questionnaire: "/questionnaire",
	book_appointment: "/get-ready/tasks/book-appointment",
	upload_documents: "/get-ready/tasks/upload-documents",
	sign_consent: "/get-ready/tasks/sign-consent",
};

const TASK_ID_ROUTE_BY_KEY: Readonly<
	Record<string, (taskId: string) => string>
> = {
	book_appointment: (taskId) => `/get-ready/tasks/${taskId}/book-appointment`,
	upload_documents: (taskId) => `/get-ready/tasks/${taskId}/upload-documents`,
	sign_consent: (taskId) => `/get-ready/tasks/${taskId}/sign-consent`,
};

export interface GetTaskRouteInput {
	taskKey: string;
	id?: string;
}

/**
 * Returns the route for a task instance, or undefined if no dedicated route exists.
 *
 * If an id is present, task-action pages receive it in the route so follow-up
 * slices can submit mutations against a durable backend task instance. Static
 * routes remain as fallbacks for pre-M005 callers and direct links.
 */
export function getTaskRoute(task: GetTaskRouteInput): string | undefined {
	if (task.id) {
		const buildTaskIdRoute = TASK_ID_ROUTE_BY_KEY[task.taskKey];
		if (buildTaskIdRoute) return buildTaskIdRoute(task.id);
	}
	return TASK_ROUTE_BY_KEY[task.taskKey];
}

/**
 * Task keys that have Ask Eve prefill questions wired in i18n.
 * The i18n key pattern is: getReady.askEve.<taskKey>
 * If a task key is not listed here, the Ask Eve button is hidden.
 */
export const ASK_EVE_WIRED_TASK_KEYS: ReadonlySet<string> = new Set([
	"fill_health_questionnaire",
	"book_appointment",
	"upload_documents",
	"sign_consent",
]);

/**
 * Returns the i18n key for an Ask Eve prefill question, or undefined if not wired.
 */
export function getAskEveI18nKey(taskKey: string): string | undefined {
	if (ASK_EVE_WIRED_TASK_KEYS.has(taskKey)) {
		return `askEve.${taskKey}`;
	}
	return undefined;
}
